"use client"

import {
    type FieldError,
    type FieldErrorCode
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorCodes"
import {
    createContext,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode
} from "react"

export interface FieldValidationHandle {
    /** Drops surrounding whitespace, so the required check sees a blank as blank. */
    trim: () => void
    validate: () => boolean
    focus: () => void
    setExternalErrors: (errors: Array<FieldErrorCode | FieldError>) => void
    /** Drops validator messages and advisory flags on this field. */
    clear: () => void
}

/** Written every render by the field, so the registry always calls the current closures. */
export type FieldValidationHandleRef = { readonly current: FieldValidationHandle | null }

interface FieldValidationActions {
    register: (name: string, handle: FieldValidationHandleRef) => () => void
    /** Validates every registered field and focuses the first that fails. */
    validateAll: () => boolean
    setFieldErrors: (name: string, errors: Array<FieldErrorCode | FieldError>) => void
    /**
     * Drops registry-pushed messages (Loqate flags) on every field. Does not touch
     * native / validator errors, or restrictions the owner still declares each render.
     */
    clearReportedErrors: () => void
    focusField: (name: string) => void
    reportBlocking: (name: string, isBlocking: boolean) => void
}

/**
 * Split from the state below because fields subscribe to it: were the two together, every
 * change of blocking state would hand each field a new object and re-run its registration.
 */
const FieldValidationActionsContext = createContext<FieldValidationActions | null>(null)
const FieldValidationStateContext = createContext<{ hasBlockingError: boolean }>({
    hasBlockingError: false
})

/**
 * Optional on purpose: inputs outside the checkout form validate themselves and never see
 * a provider.
 */
export const useFieldValidationActions = () => useContext(FieldValidationActionsContext)

export const useFieldValidationState = () => useContext(FieldValidationStateContext)

export function FieldValidationProvider({ children }: { children: ReactNode }) {
    const fields = useRef(new Map<string, FieldValidationHandleRef>())
    const [blockedBy, setBlockedBy] = useState<readonly string[]>([])

    const actions = useMemo<FieldValidationActions>(() => {
        const dropBlocking = (name: string) =>
            setBlockedBy(current =>
                current.includes(name) ? current.filter(entry => entry !== name) : current
            )

        return {
            register: (name, handle) => {
                fields.current.set(name, handle)
                return () => {
                    fields.current.delete(name)
                    dropBlocking(name)
                }
            },
            reportBlocking: (name, isBlocking) =>
                setBlockedBy(current => {
                    if (isBlocking === current.includes(name)) return current
                    return isBlocking
                        ? [...current, name]
                        : current.filter(entry => entry !== name)
                }),
            validateAll: () => {
                const handles = [...fields.current.values()]
                handles.forEach(handle => handle.current?.trim())

                // `filter` runs every field, so the customer sees all of the problems at
                // once rather than one per attempt.
                const firstInvalid = handles
                    .filter(handle => handle.current?.validate() === false)
                    .at(0)

                firstInvalid?.current?.focus()
                return !firstInvalid
            },
            setFieldErrors: (name, errors) =>
                fields.current.get(name)?.current?.setExternalErrors(errors),
            clearReportedErrors: () =>
                fields.current.forEach(handle => {
                    handle.current?.clear()
                    handle.current?.setExternalErrors([])
                }),
            focusField: name => fields.current.get(name)?.current?.focus()
        }
    }, [])

    const state = useMemo(() => ({ hasBlockingError: blockedBy.length > 0 }), [blockedBy])

    return (
        <FieldValidationActionsContext.Provider value={actions}>
            <FieldValidationStateContext.Provider value={state}>
                {children}
            </FieldValidationStateContext.Provider>
        </FieldValidationActionsContext.Provider>
    )
}
